import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { PiFolderSimple, PiTrash, PiCaretRight, PiCircleNotch, PiDatabase, PiCopySimple, PiDownloadSimple, PiMagnifyingGlass, PiMagicWand } from "react-icons/pi";

import API from "../../api";
import { capture } from "../../instrument";
import { uploadFilesToFolder } from "../../components/annotation/importAnnotationFiles";

const sourceLabels = { manual: "Manuel", harvest: "Récolte auto", script: "Script" };

const AnnotationFolders = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFolder, setNewFolder] = useState({ name: "", type: "city" });
  const [uploadingFolderId, setUploadingFolderId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [showDatasetModal, setShowDatasetModal] = useState(false);
  const [newDataset, setNewDataset] = useState({ name: "", type: "city", folderIds: [] });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({ templateDir: "", indexName: "", count: 50, folderName: "" });
  const [folderSearch, setFolderSearch] = useState("");
  const [folderType, setFolderType] = useState("all");
  const [folderSource, setFolderSource] = useState("all");
  const [onlyRemaining, setOnlyRemaining] = useState(false);
  const [preAnnotateTarget, setPreAnnotateTarget] = useState(null);
  const [preAnnotateForm, setPreAnnotateForm] = useState({ repo: "", revision: "" });

  const { data: folders = [], isLoading } = useQuery({
    queryKey: ["annotation-folders"],
    queryFn: async () => {
      const response = await API.get("/annotation-folder");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    refetchInterval: (query) => (query.state.data?.some((folder) => folder.preAnnotate?.status === "running") ? 3000 : false),
  });

  const { data: datasets = [] } = useQuery({
    queryKey: ["annotation-datasets"],
    queryFn: async () => {
      const response = await API.get("/annotation-dataset");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    refetchInterval: (query) => (query.state.data?.some((dataset) => dataset.status === "building") ? 3000 : false),
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["synthetic-templates"],
    queryFn: async () => {
      const response = await API.get("/synthetic-template");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });

  const createDataset = useMutation({
    mutationFn: async () => {
      const response = await API.post("/annotation-dataset", newDataset);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["annotation-datasets"] });
      setShowDatasetModal(false);
      setNewDataset({ name: "", type: "city", folderIds: [] });
      toast.success("Construction du dataset lancée");
    },
    onError: (error) => toast.error(error.message || "Erreur lors de la création du dataset"),
  });

  const onDeleteDataset = async (dataset) => {
    const confirmed = confirm(`Supprimer le dataset « ${dataset.name} » ?`);
    if (!confirmed) return;

    try {
      const response = await API.delete(`/annotation-dataset/${dataset._id}`);
      if (!response.ok) throw new Error(response.message);
      toast.success("Dataset supprimé");
      queryClient.invalidateQueries({ queryKey: ["annotation-datasets"] });
    } catch (error) {
      capture(error);
      toast.error(error.message || "Erreur lors de la suppression du dataset");
    }
  };

  const launchPreAnnotate = async () => {
    try {
      const response = await API.post(`/annotation-folder/${preAnnotateTarget._id}/pre-annotate`, { repo: preAnnotateForm.repo, revision: preAnnotateForm.revision });
      if (!response.ok) throw new Error(response.message);
      toast.success("Pré-annotation lancée");
      setPreAnnotateTarget(null);
      setPreAnnotateForm({ repo: "", revision: "" });
      queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
    } catch (error) {
      capture(error);
      toast.error(error.message || "Erreur lors du lancement de la pré-annotation");
    }
  };

  const onCopyTrainCommand = async (dataset) => {
    await navigator.clipboard.writeText(dataset.trainCommand);
    toast.success("Commande d'entraînement copiée — à coller sur une machine GPU vierge");
  };

  const selectedTemplate = templates.find((template) => template.dir === generateForm.templateDir);
  const generateCommand =
    selectedTemplate && generateForm.indexName.trim()
      ? `node src/script/synthetic/generate.js --template '${selectedTemplate.dir}' --index-name '${generateForm.indexName.trim()}' --count ${generateForm.count}${
          generateForm.folderName.trim() ? ` --folder '${generateForm.folderName.trim()}'` : ""
        }`
      : "";

  const onCopyGenerateCommand = async () => {
    await navigator.clipboard.writeText(generateCommand);
    toast.success("Commande de génération copiée — à lancer en local depuis api/");
  };

  const normalize = (value) => value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filteredFolders = folders.filter((folder) => {
    if (folderSearch && !normalize(folder.name).includes(normalize(folderSearch))) return false;
    if (folderType !== "all" && folder.type !== folderType) return false;
    if (folderSource !== "all" && (folder.source || "manual") !== folderSource) return false;
    if (onlyRemaining && folder.total - folder.reviewed <= 0) return false;
    return true;
  });

  const createFolder = useMutation({
    mutationFn: async () => {
      const response = await API.post("/annotation-folder", newFolder);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    onSuccess: (folder) => {
      queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
      setShowCreateModal(false);
      setNewFolder({ name: "", type: "city" });
      history.push(`/annotation/${folder._id}`);
    },
    onError: (error) => toast.error(error.message || "Erreur lors de la création du dossier"),
  });

  const onDeleteFolder = async (event, folder) => {
    event.stopPropagation();
    const confirmed = confirm(`Supprimer le dossier « ${folder.name} » et ses ${folder.total} document(s) ?`);
    if (!confirmed) return;

    try {
      const response = await API.delete(`/annotation-folder/${folder._id}`);
      if (!response.ok) throw new Error(response.message);
      toast.success("Dossier supprimé");
      queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
    } catch (error) {
      capture(error);
      toast.error(error.message || "Erreur lors de la suppression du dossier");
    }
  };

  const onDropFiles = async (event, folder) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverFolderId(null);

    const files = Array.from(event.dataTransfer.files || []);
    if (files.length === 0) return;

    setUploadingFolderId(folder._id);
    try {
      const uploaded = await uploadFilesToFolder(files, folder._id);
      toast.success(`${uploaded} document(s) importé(s) dans « ${folder.name} »`);
      queryClient.invalidateQueries({ queryKey: ["annotation-folders"] });
    } catch (error) {
      capture(error);
      toast.error(error.message || "Erreur lors de l'import");
    } finally {
      setUploadingFolderId(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-baseline gap-2.5">
          <h1 className="text-xl font-semibold text-gray-900">Annotation</h1>
          <span className="text-sm tabular-nums text-gray-400">{filteredFolders.length === folders.length ? `${folders.length} dossier(s)` : `${filteredFolders.length}/${folders.length} dossier(s)`}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
            onClick={() => setShowGenerateModal(true)}
          >
            <PiMagicWand className="h-4 w-4" />
            Générer
          </button>
          <button type="button" className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700" onClick={() => setShowCreateModal(true)}>
            Nouveau dossier
          </button>
        </div>
      </div>

      <p className="mb-4 text-sm text-gray-500">Glissez des documents sur un dossier pour les importer. Les dossiers « Récolte auto » sont alimentés chaque nuit depuis la production.</p>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5">
          <PiMagnifyingGlass className="h-4 w-4 shrink-0 text-gray-400" />
          <input
            type="text"
            name="folderSearch"
            aria-label="Rechercher un dossier"
            value={folderSearch}
            onChange={(event) => setFolderSearch(event.target.value)}
            placeholder="Rechercher un dossier…"
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
            autoFocus
          />

          <button
            type="button"
            className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
              onlyRemaining ? "border-primary-200 bg-primary-50 text-primary-700" : "border-gray-200 text-gray-500 hover:bg-gray-50"
            }`}
            onClick={() => setOnlyRemaining((previous) => !previous)}
          >
            À réviser ({folders.filter((folder) => folder.total - folder.reviewed > 0).length})
          </button>

          <div className="flex shrink-0 rounded-md border border-gray-200 p-0.5">
            {[
              { key: "all", label: "Tous" },
              { key: "city", label: "Ville" },
              { key: "clinic", label: "Clinique" },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                className={`rounded px-2 py-0.5 text-xs font-medium ${folderType === option.key ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setFolderType(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <select
            name="folderSource"
            aria-label="Filtrer par source"
            value={folderSource}
            onChange={(event) => setFolderSource(event.target.value)}
            className="shrink-0 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 outline-none"
          >
            <option value="all">Toutes les sources</option>
            <option value="manual">Manuel</option>
            <option value="harvest">Récolte auto</option>
            <option value="script">Script</option>
          </select>
        </div>

        {isLoading && <div className="py-16 text-center text-gray-400">Chargement…</div>}

        {!isLoading && folders.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-16 text-gray-400">
            <PiFolderSimple className="h-7 w-7 text-gray-300" />
            <div className="text-sm">Aucun dossier — créez-en un pour commencer à annoter</div>
          </div>
        )}

        {!isLoading && folders.length > 0 && filteredFolders.length === 0 && (
          <div className="py-16 text-center text-sm text-gray-400">Aucun dossier ne correspond à ces filtres</div>
        )}

        <div className="divide-y divide-gray-100" role="list">
          {filteredFolders.map((folder) => {
            const progress = folder.total > 0 ? Math.round((folder.reviewed / folder.total) * 100) : 0;
            const remaining = folder.total - folder.reviewed;

            return (
              <div
                key={folder._id}
                className={`group flex h-[53px] cursor-pointer items-center gap-3 px-4 ${dragOverFolderId === folder._id ? "bg-primary-50/50" : "hover:bg-gray-50"}`}
                onClick={() => history.push(`/annotation/${folder._id}`)}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOverFolderId(folder._id);
                }}
                onDragLeave={() => setDragOverFolderId(null)}
                onDrop={(event) => onDropFiles(event, folder)}
              >
                <PiFolderSimple className="h-4 w-4 shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{folder.name}</div>

                <div className="hidden shrink-0 items-center gap-1.5 text-xs text-gray-400 sm:flex">
                  <span>{folder.type === "clinic" ? "Clinique" : "Ville"}</span>
                  <span>·</span>
                  <span>{sourceLabels[folder.source] || sourceLabels.manual}</span>
                </div>

                {uploadingFolderId === folder._id ? (
                  <div className="flex shrink-0 items-center gap-1.5 text-xs text-primary-700">
                    <PiCircleNotch className="h-3.5 w-3.5 animate-spin" />
                    Import…
                  </div>
                ) : folder.preAnnotate?.status === "running" ? (
                  <div className="flex shrink-0 items-center gap-1.5 text-xs text-primary-700">
                    <PiCircleNotch className="h-3.5 w-3.5 animate-spin" />
                    Pré-annotation {folder.preAnnotate.done}/{folder.preAnnotate.total}
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center gap-2.5">
                    {folder.preAnnotate?.status === "failed" && (
                      <span className="text-xs font-medium text-red-600" title={folder.preAnnotate.error}>
                        Pré-annotation échouée
                      </span>
                    )}
                    {remaining > 0 && <span className="text-xs font-medium tabular-nums text-gray-900">{remaining} à réviser</span>}
                    <div className="h-1 w-24 overflow-hidden rounded-full bg-gray-100">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="w-14 text-right text-xs tabular-nums text-gray-500">
                      {folder.reviewed}/{folder.total}
                    </span>
                  </div>
                )}

                <button
                  type="button"
                  aria-label="Pré-annoter avec un modèle"
                  title="Pré-annoter avec un modèle"
                  className="shrink-0 rounded-md p-1.5 text-gray-300 hover:bg-primary-50 hover:text-primary-600 disabled:opacity-40"
                  disabled={folder.preAnnotate?.status === "running"}
                  onClick={(event) => {
                    event.stopPropagation();
                    setPreAnnotateForm({ repo: folder.preAnnotate?.repo || "", revision: folder.preAnnotate?.revision || "" });
                    setPreAnnotateTarget(folder);
                  }}
                >
                  <PiMagicWand className="h-3.5 w-3.5" />
                </button>

                <button
                  type="button"
                  aria-label="Supprimer le dossier"
                  className="shrink-0 rounded-md p-1.5 text-gray-300 hover:bg-red-50 hover:text-red-600"
                  onClick={(event) => onDeleteFolder(event, folder)}
                >
                  <PiTrash className="h-3.5 w-3.5" />
                </button>
                <PiCaretRight className="h-3.5 w-3.5 shrink-0 text-gray-300" />
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-3 mt-8 flex items-center justify-between">
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-lg font-medium text-gray-900">Datasets</h2>
          <span className="text-sm tabular-nums text-gray-400">{datasets.length}</span>
        </div>
        <button type="button" className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" onClick={() => setShowDatasetModal(true)}>
          Créer un dataset
        </button>
      </div>
      <p className="mb-4 text-sm text-gray-500">
        Un dataset fige les documents révisés (répartition train/val/test incluse) avec son script d'entraînement. La commande copiée se colle telle quelle sur une machine GPU
        vierge (Lambda, etc.) : installation, téléchargement, connexions W&B / Hugging Face et entraînement.
      </p>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        {datasets.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
            <PiDatabase className="h-7 w-7 text-gray-300" />
            <div className="text-sm">Aucun dataset — créez-en un à partir de vos documents révisés</div>
          </div>
        )}

        <div className="divide-y divide-gray-100" role="list">
          {datasets.map((dataset) => (
            <div key={dataset._id} className="flex h-[53px] items-center gap-3 px-4">
              <PiDatabase className="h-4 w-4 shrink-0 text-gray-400" />
              <div className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{dataset.name}</div>

              <div className="hidden shrink-0 items-center gap-1.5 text-xs text-gray-400 sm:flex">
                <span>{dataset.type === "clinic" ? "Clinique" : "Ville"}</span>
                <span>·</span>
                <span>{dataset.folderIds?.length > 0 ? `${dataset.folderIds.length} dossier(s)` : "Tous les dossiers"}</span>
              </div>

              {dataset.status === "building" && (
                <div className="flex shrink-0 items-center gap-1.5 text-xs text-gray-500">
                  <PiCircleNotch className="h-3.5 w-3.5 animate-spin" />
                  Construction…
                </div>
              )}
              {dataset.status === "failed" && (
                <span className="shrink-0 text-xs font-medium text-red-600" title={dataset.error}>
                  Échec
                </span>
              )}
              {dataset.status === "ready" && (
                <span className="shrink-0 text-xs tabular-nums text-gray-500">
                  {dataset.counts?.total || 0} docs · {dataset.counts?.train || 0}/{dataset.counts?.val || 0}/{dataset.counts?.test || 0}
                </span>
              )}

              {dataset.status === "ready" && (
                <>
                  <button
                    type="button"
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
                    onClick={() => onCopyTrainCommand(dataset)}
                  >
                    <PiCopySimple className="h-3.5 w-3.5" />
                    Commande d'entraînement
                  </button>
                  <a
                    href={dataset.downloadUrl}
                    className="shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                    title="Télécharger le dataset (.jsonl)"
                    download
                  >
                    <PiDownloadSimple className="h-3.5 w-3.5" />
                  </a>
                </>
              )}

              <button
                type="button"
                aria-label="Supprimer le dataset"
                className="shrink-0 rounded-md p-1.5 text-gray-300 hover:bg-red-50 hover:text-red-600"
                onClick={() => onDeleteDataset(dataset)}
              >
                <PiTrash className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {preAnnotateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="min-w-[500px] rounded-lg bg-white px-6 py-4">
            <h2 className="text-lg font-medium text-gray-900">Pré-annoter « {preAnnotateTarget.name} »</h2>
            <p className="mt-1 text-sm text-gray-500">
              Le modèle annote les {preAnnotateTarget.total - preAnnotateTarget.reviewed} document(s) non révisés ; ses prédictions apparaissent comme recommandations dans l'éditeur (À réviser).
            </p>

            <label className="mt-4 block text-xs font-medium text-gray-600" htmlFor="preAnnotateRepo">
              Dépôt Hugging Face
            </label>
            <input
              id="preAnnotateRepo"
              type="text"
              name="preAnnotateRepo"
              value={preAnnotateForm.repo}
              onChange={(event) => setPreAnnotateForm((previous) => ({ ...previous, repo: event.target.value }))}
              placeholder="ex : lucienklein/extraction-clinic-lilt"
              className="mt-1 w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              autoFocus
            />

            <label className="mt-3 block text-xs font-medium text-gray-600" htmlFor="preAnnotateRevision">
              Commit / révision <span className="text-gray-400">(optionnel — défaut : main)</span>
            </label>
            <input
              id="preAnnotateRevision"
              type="text"
              name="preAnnotateRevision"
              value={preAnnotateForm.revision}
              onChange={(event) => setPreAnnotateForm((previous) => ({ ...previous, revision: event.target.value }))}
              placeholder="ex : 6e41f6ce… ou main"
              className="mt-1 w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" onClick={() => setPreAnnotateTarget(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                disabled={!preAnnotateForm.repo.trim()}
                onClick={launchPreAnnotate}
              >
                Lancer
              </button>
            </div>
          </div>
        </div>
      )}

      {showDatasetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="min-w-[500px] rounded-lg bg-white px-6 py-4">
            <h2 className="mb-4 text-lg font-medium text-gray-900">Créer un dataset</h2>
            <input
              type="text"
              name="datasetName"
              aria-label="Nom du dataset"
              value={newDataset.name}
              onChange={(event) => setNewDataset((previous) => ({ ...previous, name: event.target.value }))}
              placeholder="Nom du dataset (ex: lilt-city-2026-06)"
              className="w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
            <div className="mt-3 flex gap-2">
              {["city", "clinic"].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`flex-1 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                    newDataset.type === type ? "border-primary-500 bg-primary-50 text-primary-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                  onClick={() => setNewDataset((previous) => ({ ...previous, type, folderIds: [] }))}
                >
                  {type === "clinic" ? "Clinique" : "Ville"}
                </button>
              ))}
            </div>

            <div className="mt-3 max-h-48 overflow-y-auto rounded-md border border-gray-200">
              {folders
                .filter((folder) => folder.type === newDataset.type)
                .map((folder) => {
                  const checked = newDataset.folderIds.includes(folder._id);
                  return (
                    <label key={folder._id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        name={`dataset-folder-${folder._id}`}
                        checked={checked}
                        onChange={() =>
                          setNewDataset((previous) => ({
                            ...previous,
                            folderIds: checked ? previous.folderIds.filter((id) => id !== folder._id) : [...previous.folderIds, folder._id],
                          }))
                        }
                      />
                      <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                      <span className="shrink-0 text-xs tabular-nums text-gray-400">{folder.reviewed} révisés</span>
                    </label>
                  );
                })}
            </div>
            <p className="mt-2 text-xs text-gray-500">Aucune sélection = tous les dossiers du type. Seuls les documents révisés sont inclus.</p>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" onClick={() => setShowDatasetModal(false)}>
                Annuler
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                disabled={!newDataset.name.trim() || createDataset.isPending}
                onClick={() => createDataset.mutate()}
              >
                {createDataset.isPending ? "Création…" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="min-w-[500px] rounded-lg bg-white px-6 py-4">
            <h2 className="mb-4 text-lg font-medium text-gray-900">Nouveau dossier</h2>
            <input
              type="text"
              name="folderName"
              aria-label="Nom du dossier"
              value={newFolder.name}
              onChange={(event) => setNewFolder((previous) => ({ ...previous, name: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === "Enter" && newFolder.name.trim()) createFolder.mutate();
              }}
              placeholder="Nom du dossier"
              className="w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
            <div className="mt-3 flex gap-2">
              {["city", "clinic"].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`flex-1 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                    newFolder.type === type ? "border-primary-500 bg-primary-50 text-primary-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                  onClick={() => setNewFolder((previous) => ({ ...previous, type }))}
                >
                  {type === "clinic" ? "Clinique" : "Ville"}
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" onClick={() => setShowCreateModal(false)}>
                Annuler
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                disabled={!newFolder.name.trim() || createFolder.isPending}
                onClick={() => createFolder.mutate()}
              >
                {createFolder.isPending ? "Création…" : "Créer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="min-w-[560px] max-w-[640px] rounded-lg bg-white px-6 py-4">
            <h2 className="mb-1 text-lg font-medium text-gray-900">Générer des données synthétiques</h2>
            <p className="mb-4 text-sm text-gray-500">
              Choisissez un modèle, copiez la commande et lancez-la en local sur votre Mac (depuis <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">api/</code>). Le dossier généré
              apparaît ci-dessus, prêt à être révisé.
            </p>

            <label className="block text-xs font-medium text-gray-600" htmlFor="generateTemplate">
              Modèle
            </label>
            {templates.length === 0 ? (
              <p className="mt-1 rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-500">
                Aucun modèle — créez-en un avec la skill <code>prescription-template</code>, dans <code>api/src/script/synthetic/templates/</code>.
              </p>
            ) : (
              <select
                id="generateTemplate"
                name="generateTemplate"
                value={generateForm.templateDir}
                onChange={(event) => setGenerateForm((previous) => ({ ...previous, templateDir: event.target.value }))}
                className="mt-1 w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sélectionner un modèle…</option>
                {templates.map((template) => (
                  <option key={template.dir} value={template.dir}>
                    {template.name} ({template.type === "clinic" ? "Clinique" : "Ville"})
                  </option>
                ))}
              </select>
            )}

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600" htmlFor="generateIndexName">
                  indexName
                </label>
                <input
                  id="generateIndexName"
                  type="text"
                  name="generateIndexName"
                  value={generateForm.indexName}
                  onChange={(event) => setGenerateForm((previous) => ({ ...previous, indexName: event.target.value }))}
                  placeholder="ex : atmir"
                  className="mt-1 w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600" htmlFor="generateCount">
                  Nombre de documents
                </label>
                <input
                  id="generateCount"
                  type="number"
                  min="1"
                  name="generateCount"
                  value={generateForm.count}
                  onChange={(event) => setGenerateForm((previous) => ({ ...previous, count: Math.max(1, parseInt(event.target.value, 10) || 1) }))}
                  className="mt-1 w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <label className="mt-3 block text-xs font-medium text-gray-600" htmlFor="generateFolder">
              Nom du dossier <span className="text-gray-400">(optionnel)</span>
            </label>
            <input
              id="generateFolder"
              type="text"
              name="generateFolder"
              value={generateForm.folderName}
              onChange={(event) => setGenerateForm((previous) => ({ ...previous, folderName: event.target.value }))}
              placeholder="défaut : synthetic-<modèle>-<date>"
              className="mt-1 w-full rounded-md border border-gray-300 p-1.5 text-sm outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />

            <label className="mt-4 block text-xs font-medium text-gray-600">Commande</label>
            <div className="mt-1 rounded-md border border-gray-200 bg-gray-900 p-3">
              <code className="block whitespace-pre-wrap break-all font-mono text-xs leading-relaxed text-gray-100">
                {generateCommand || "Sélectionnez un modèle et renseignez l'indexName…"}
              </code>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50" onClick={() => setShowGenerateModal(false)}>
                Fermer
              </button>
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                disabled={!generateCommand}
                onClick={onCopyGenerateCommand}
              >
                <PiCopySimple className="h-4 w-4" />
                Copier la commande
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnotationFolders;
